import { X, Check, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const BEFORE = [
  "Reuniones largas",
  "Prioridades difusas",
  "Decisiones reactivas",
  "Problemas repetidos",
  "Responsabilidades poco claras",
];
const AFTER = [
  "Prioridades claras",
  "Líderes accountable",
  "Métricas visibles",
  "Reuniones efectivas",
  "Problemas resueltos de raíz",
];

const HOME_COPY = {
  title: <>Cuando EOS empieza a funcionar,{" "}<span className="text-[#F26522]">la empresa deja de depender de ti para avanzar.</span></>,
  beforeHeadline: "Todo depende demasiado de ti.",
  afterHeadline: "La empresa empieza a funcionar con más control y menos dependencia.",
  before: [
    "Las decisiones se concentran arriba.",
    "Las prioridades cambian seguido.",
    "Los problemas vuelven una y otra vez.",
    "Hay reuniones, pero poca ejecución.",
    "No siempre está claro quién responde por qué.",
  ],
  after: [
    "El equipo sabe hacia dónde va.",
    "Las prioridades son claras.",
    "Cada persona sabe qué debe lograr.",
    "Los problemas se enfrentan y resuelven.",
    "La ejecución deja de depender de empujar todo personalmente.",
  ],
};

export default function BeforeAfter({ variant }) {
  const copy = variant === "home" ? HOME_COPY : null;
  const beforeItems = copy ? copy.before : BEFORE;
  const afterItems = copy ? copy.after : AFTER;
  return (
    <section data-testid="before-after-section" className="section-padding bg-white">
      <div className="container-eos">
        <div className="max-w-3xl reveal">
          <span className="eyebrow">El cambio</span>
          <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy-800 leading-tight">
            {copy ? copy.title : (<>Cuando EOS empieza a funcionar,{" "}
            <span className="text-[#F26522]">el negocio se siente distinto.</span></>)}
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-7">
          {/* Before */}
          <div className="reveal rounded-2xl border border-slate-200 bg-slate-50/70 p-5 lg:p-7">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold tracking-[0.22em] uppercase text-slate-500">Antes</span>
              <span className="font-display font-bold text-slate-300 text-xl">Sin EOS</span>
            </div>
            <p className="mt-3 font-display font-extrabold text-2xl text-slate-700 leading-tight">
              {copy ? copy.beforeHeadline : "Crecimiento que se siente como caos."}
            </p>
            <ul className="mt-5 space-y-2" data-testid="before-list">
              {beforeItems.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-600">
                  <span className="mt-0.5 inline-flex w-6 h-6 rounded-md bg-slate-200 items-center justify-center shrink-0">
                    <X className="w-3.5 h-3.5 text-slate-500" strokeWidth={2.4} />
                  </span>
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* After */}
          <div
            className="reveal rounded-2xl border border-[#F26522]/20 bg-[#FEF3EB] p-5 lg:p-7"
            style={{ transitionDelay: "120ms" }}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#F26522]">Después</span>
              <ConEosBadge />
            </div>
            <p className="mt-3 font-display font-extrabold text-2xl text-navy-800 leading-tight">
              {copy ? copy.afterHeadline : "Crecimiento con foco, ritmo y disciplina."}
            </p>
            <ul className="mt-5 space-y-2" data-testid="after-list">
              {afterItems.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-navy-700">
                  <span className="mt-0.5 inline-flex w-6 h-6 rounded-md bg-[#F26522] items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.8} />
                  </span>
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function ConEosBadge() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!("IntersectionObserver" in window)) { setVisible(true); return; }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } }),
      { threshold: 0.6 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className="relative inline-flex" data-testid="con-eos-badge">
      {visible && (
        <span
          aria-hidden="true"
          className="absolute -inset-2 rounded-full bg-[#F26522]/40 blur-md animate-badge-glow"
        />
      )}
      <span
        className={`relative inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#F26522] text-white font-display font-extrabold text-sm tracking-wide shadow-lg shadow-[#F26522]/30 ring-2 ring-[#F26522]/40 ${
          visible ? "animate-badge-pop" : "opacity-0"
        }`}
      >
        <Sparkles
          className={`w-4 h-4 text-white ${visible ? "animate-badge-sparkle" : ""}`}
          strokeWidth={2.4}
        />
        Con EOS
      </span>
    </div>
  );
}

